// hooks/useEnhancedUserBubble.js - Enhanced user bubble logic
// Extracted from PlatformSelector.js and Dashboard.js

import { useState, useEffect, useCallback } from 'react';
import { useSupabaseAuth } from './useSupabaseAuth';
import logger from '../services/logger';

/**
 * Custom hook for enhanced user bubble functionality
 */
export const useEnhancedUserBubble = (navigate, currentPage = 'platforms') => {
  // Get Supabase auth state
  const { user: supabaseUser, isAuthenticated } = useSupabaseAuth();

  // User and session state
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);

  // Dropdown states
  const [showMongoDropdown, setShowMongoDropdown] = useState(false);
  const [showSpotifyDropdown, setShowSpotifyDropdown] = useState(false);
  const [showYouTubeDropdown, setShowYouTubeDropdown] = useState(false);
  const [showMongoLogoutConfirm, setShowMongoLogoutConfirm] = useState(false);

  /**
   * Initialize user data from Supabase auth
   */
  const initializeUserData = useCallback(() => {
    if (isAuthenticated && supabaseUser) {
      // User is authenticated with Supabase
      setUser({
        email: supabaseUser.email,
        displayName: supabaseUser.user_metadata?.display_name || supabaseUser.email?.split('@')[0] || 'User',
        id: supabaseUser.id
      });
      setIsGuest(false);
      return true;
    } else {
      // No Supabase session, treat as guest
      setIsGuest(true);
      return false;
    }
  }, [isAuthenticated, supabaseUser]);

  /**
   * Click outside handler to close dropdowns
   */
  const handleClickOutside = useCallback((event) => {
    if (!event.target.closest('.user-bubble-container')) {
      setShowMongoDropdown(false);
      setShowSpotifyDropdown(false);
      setShowYouTubeDropdown(false);
      setShowMongoLogoutConfirm(false);
    }
  }, []);

  /**
   * Platform switching handler for bubble
   */
  const handleBubblePlatformSwitch = useCallback(async (platform, onPlatformSwitch) => {
    try {
      logger.progress(`Switching to ${platform} from bubble...`);
      
      // Close all dropdowns
      setShowSpotifyDropdown(false);
      setShowYouTubeDropdown(false);
      
      // Call parent function if provided
      if (onPlatformSwitch) {
        await onPlatformSwitch(platform);
      }
      
    } catch (error) {
      logger.error(`Error switching to ${platform} from bubble:`, error);
      // Fallback: navigate to dashboard
      navigate('/dashboard');
    }
  }, [navigate]);

  /**
   * Platform logout handler for bubble
   */
  const handleBubblePlatformLogout = useCallback(async (platform, onPlatformLogout) => {
    try {
      logger.progress(`Logging out of ${platform} from bubble...`);
      
      // Close dropdowns
      setShowSpotifyDropdown(false);
      setShowYouTubeDropdown(false);
      
      // Call parent function if provided
      if (onPlatformLogout) {
        await onPlatformLogout(platform);
      }
      
    } catch (error) {
      logger.error(`Error logging out of ${platform} from bubble:`, error);
    }
  }, []);

  /**
   * Back to platforms handler with cleanup support
   */
  const handleBackToPlatforms = useCallback(async (onBackToPlatforms) => {
    logger.debug('Back to Platforms clicked from bubble...');
    
    // Close all dropdowns first
    setShowMongoDropdown(false);
    setShowSpotifyDropdown(false);
    setShowYouTubeDropdown(false);
    
    // If we're on dashboard, use enhanced cleanup
    if (currentPage === 'dashboard') {
      logger.debug('Dashboard detected - performing enhanced cleanup...');
      
      if (onBackToPlatforms) {
        // Use custom dashboard cleanup handler
        await onBackToPlatforms();
      } else {
        // Fallback: just navigate
        navigate('/platforms');
      }
    } else {
      // We're already on platforms page, just close dropdowns
      logger.debug('Already on platforms page');
    }
  }, [currentPage, navigate]);

  /**
   * MongoDB logout confirmation handler
   */
  const handleMongoLogoutConfirm = useCallback((onMongoLogout) => {
    setShowMongoLogoutConfirm(false);
    if (onMongoLogout) {
      onMongoLogout();
    }
  }, []);

  /**
   * Toggle dropdown functions
   */
  const toggleMongoDropdown = useCallback(() => {
    setShowMongoDropdown(prev => !prev);
    setShowSpotifyDropdown(false);
    setShowYouTubeDropdown(false);
  }, []);

  const toggleSpotifyDropdown = useCallback(() => {
    setShowSpotifyDropdown(prev => !prev);
    setShowMongoDropdown(false);
    setShowYouTubeDropdown(false);
  }, []);

  const toggleYouTubeDropdown = useCallback(() => {
    setShowYouTubeDropdown(prev => !prev);
    setShowMongoDropdown(false);
    setShowSpotifyDropdown(false);
  }, []);

  /**
   * Close all dropdowns
   */
  const closeAllDropdowns = useCallback(() => {
    setShowMongoDropdown(false);
    setShowSpotifyDropdown(false);
    setShowYouTubeDropdown(false);
    setShowMongoLogoutConfirm(false);
  }, []);

  /**
   * Handle create account action
   */
  const handleCreateAccount = useCallback(() => {
    closeAllDropdowns();
    navigate('/');
  }, [closeAllDropdowns, navigate]);

  /**
   * Handle logout confirmation display
   */
  const handleShowLogoutConfirm = useCallback(() => {
    setShowMongoDropdown(false);
    setShowMongoLogoutConfirm(true);
  }, []);

  /**
   * Cancel logout confirmation
   */
  const handleCancelLogout = useCallback(() => {
    setShowMongoLogoutConfirm(false);
  }, []);

  /**
   * Get user display name
   */
  const getUserDisplayName = useCallback(() => {
    if (user) {
      return user.displayName || user.email || 'User';
    }
    return 'Guest User';
  }, [user]);

  /**
   * Get user avatar letter
   */
  const getUserAvatarLetter = useCallback(() => {
    if (user) {
      return user.displayName?.[0]?.toUpperCase() || 
             user.email?.[0]?.toUpperCase() || 
             'U';
    }
    return 'G';
  }, [user]);

  /**
   * Get user email or session info
   */
  const getUserEmailOrSession = useCallback(() => {
    if (user) {
      return user.email;
    }
    return 'Limited Session';
  }, [user]);

  // Initialize user data on mount
  useEffect(() => {
    initializeUserData();
  }, [initializeUserData]);

  // Add click outside listener
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  return {
    // State
    user,
    isGuest,
    showMongoDropdown,
    showSpotifyDropdown,
    showYouTubeDropdown,
    showMongoLogoutConfirm,
    
    // User info functions
    getUserDisplayName,
    getUserAvatarLetter,
    getUserEmailOrSession,
    
    // Action handlers
    handleBubblePlatformSwitch,
    handleBubblePlatformLogout,
    handleBackToPlatforms,
    handleMongoLogoutConfirm,
    handleCreateAccount,
    handleShowLogoutConfirm,
    handleCancelLogout,
    
    // Dropdown controls
    toggleMongoDropdown,
    toggleSpotifyDropdown,
    toggleYouTubeDropdown,
    closeAllDropdowns,
    
    // Utilities
    initializeUserData
  };
};